import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Leaf } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      const next = new URLSearchParams(loc.search).get("next");
      const defaultDest = res.user.role === "practitioner" ? "/practitioner" : "/admin";
      // Honor "next" only if it's an internal admin/practitioner path AND isn't the login page itself.
      const isSafeNext = next
        && (next.startsWith("/admin") || next.startsWith("/practitioner"))
        && !next.startsWith("/admin/login");
      nav(isSafeNext ? next : defaultDest);
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-appbg flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-10">
          <span className="w-10 h-10 rounded-pill bg-cream flex items-center justify-center">
            <Leaf className="w-5 h-5 text-forest" strokeWidth={1.5} />
          </span>
          <span className="font-display text-2xl text-charcoal">The Root Forum</span>
        </Link>

        <div className="bg-white border border-hairline rounded-lg p-8 shadow-warm">
          <h1 className="font-display text-3xl text-charcoal mb-2">Welcome back.</h1>
          <p className="text-sm font-ui text-slate mb-8">Sign in to manage your practice.</p>

          {error && (
            <div className="mb-4 px-3 py-2 bg-status-danger-bg text-status-danger text-sm rounded-md font-ui" data-testid="login-error">{error}</div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block">
              <span className="block text-sm font-ui text-charcoal mb-1.5">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className="w-full px-3 py-2.5 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30"
                data-testid="login-email-input"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-ui text-charcoal mb-1.5">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="w-full px-3 py-2.5 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30"
                data-testid="login-password-input"
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-6 py-3 bg-forest hover:bg-charcoal disabled:opacity-50 text-white rounded-md text-sm font-medium font-ui transition-colors"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="text-xs font-ui text-slate mt-6 text-center">
            Clients book without accounts — they're matched by email or phone.
          </p>
        </div>

        <Link to="/" className="block text-center text-sm font-ui text-slate hover:text-forest mt-6" data-testid="back-to-public">← Back to public site</Link>
      </div>
    </div>
  );
}
